import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Sliders, HandArrowUp } from "@phosphor-icons/react";

const TYPES = [
  { key: "issue", label: "Take from store", icon: HandArrowUp, desc: "Issue items to a staff member, patient or department" },
  { key: "usage", label: "Use / Consume", icon: ArrowDown, desc: "Debit stock used in fabrication or fittings" },
  { key: "sale", label: "Sale / Order", icon: ArrowDown, desc: "Debit stock sold to a patient or order" },
  { key: "restock", label: "Restock", icon: ArrowUp, desc: "Add newly received stock" },
  { key: "adjustment", label: "Adjust", icon: Sliders, desc: "Set the exact counted quantity" },
];

export default function TransactionDialog({ open, onOpenChange, item, onSaved, defaultType = "usage" }) {
  const [type, setType] = useState(defaultType);
  const [takenBy, setTakenBy] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setType(defaultType); setQuantity(""); setNote(""); setReference(""); setTakenBy(""); }
  }, [open, defaultType]);

  if (!item) return null;

  const submit = async () => {
    const q = Number(quantity);
    if (isNaN(q) || q < 0) { toast.error("Enter a valid quantity"); return; }
    if (type === "issue" && !takenBy.trim()) { toast.error("Enter who is taking the items"); return; }
    if (type === "issue" && q > item.quantity) { toast.error(`Only ${item.quantity} ${item.unit} available`); return; }
    setBusy(true);
    try {
      await api.post(`/inventory/${item.id}/transaction`, { type, quantity: q, note, reference, taken_by: takenBy });
      toast.success("Stock movement recorded");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="transaction-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{defaultType === "issue" ? "Take from store" : "Record movement"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="rounded-md border border-border bg-accent px-3 py-2.5">
            <p className="text-sm font-medium">{item.name}</p>
            <p className="text-xs text-muted-foreground tnums">In stock: {item.quantity} {item.unit}</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {TYPES.map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`tx-type-${t.key}`}
                onClick={() => setType(t.key)}
                className={`flex items-center gap-2 rounded-md border px-3 py-2.5 text-left text-sm transition-colors duration-200 ${
                  type === t.key ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-accent"
                }`}
              >
                <t.icon size={17} weight="bold" />
                <span className="font-medium">{t.label}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground -mt-1">{TYPES.find((t) => t.key === type)?.desc}</p>

          <div className="space-y-1.5">
            <Label>{type === "adjustment" ? "New counted quantity" : "Quantity"}</Label>
            <Input type="number" step="any" autoFocus data-testid="tx-quantity-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" />
          </div>
          {type === "issue" && (
            <div className="space-y-1.5">
              <Label>Taken by</Label>
              <Input data-testid="tx-taken-by-input" value={takenBy} onChange={(e) => setTakenBy(e.target.value)} placeholder="Staff name / department" />
            </div>
          )}
          {(type === "sale" || type === "issue") && (
            <div className="space-y-1.5">
              <Label>{type === "issue" ? "Purpose / patient" : "Reference (order / patient)"}</Label>
              <Input data-testid="tx-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Textarea data-testid="tx-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="save-transaction-button" className="bg-primary hover:bg-primary/90">
            {busy ? "Recording…" : "Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
